import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { MatDialog } from '@angular/material/dialog';
import { debounceTime, distinctUntilChanged, finalize } from 'rxjs/operators';
import { LocalConceptoComisionService, ConceptoComision } from '../../services/local-concepto-comision.service';
import { NotificationHelper } from 'src/app/core/services/helpers/notification.helper';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { showSpinner, hideSpinner } from 'src/app/core/services/functions.service';

@Component({
  selector: 'app-conceptocomisiones',
  templateUrl: './conceptocomisiones.component.html',
  styleUrls: ['./conceptocomisiones.component.scss']
})
export class ConceptocomisionesComponent implements OnInit {
  comisionForm: FormGroup;
  registros = new MatTableDataSource<ConceptoComision>([]);
  searchControl = new FormControl('');
  isLoading = false;
  editIndex: number | null = null;
  readonly DEFAULT_EMPRESA = 'JA';

  // Lista de tipos de comisión
  tiposComision = [
    { value: 'VENTA', label: 'VENTA' },
    { value: 'DEVOLUCIÓN', label: 'DEVOLUCIÓN' },
    { value: 'BONIFICACIÓN', label: 'BONIFICACIÓN' }
  ];

  // Códigos protegidos que no se pueden editar ni eliminar
  protected readonly CODIGOS_PROTEGIDOS = ['001', '002', '003', '004', '005', '006'];

  // Columnas a mostrar en la tabla
  displayedColumns: string[] = ['codigo', 'descripcion', 'tipo', 'acciones'];

  @ViewChild(MatPaginator) paginator!: MatPaginator;

  constructor(
    private fb: FormBuilder,
    private conceptoService: LocalConceptoComisionService,
    private notificationService: NotificationHelper,
    private dialog: MatDialog
  ) {
    this.initForm();
  }

  ngOnInit(): void {
    this.cargarRegistros();
    this.configurarBuscador();
  }

  ngAfterViewInit() {
    this.registros.paginator = this.paginator;
  }

  private initForm(): void {
    this.comisionForm = this.fb.group({
      codigo: [{ value: '', disabled: false }, Validators.required],
      descripcion: ['', Validators.required],
      tipo: ['', [Validators.required]]
    });
  }

  cargarRegistros(showMessage: boolean = true): void {
    showSpinner();
    this.isLoading = true;
    this.conceptoService.getConceptosActivos('JA').subscribe({
      next: (data) => {
        this.registros.data = data;
        if (showMessage) {
          this.notificationService.successMessage('Datos cargados correctamente');
        }
      },
      error: (error) => {
        this.notificationService.errorMessage('Error al cargar los registros');
      },
      complete: () => {
        this.isLoading = false;
        hideSpinner();
      }
    });
  }

  onGuardar(): void {
    if (this.comisionForm.invalid) {
      return;
    }

    // Usar getRawValue() para incluir campos deshabilitados
    const formData = this.comisionForm.getRawValue();
    const conceptoData: ConceptoComision = {
      c_emp: this.DEFAULT_EMPRESA,
      codigo: formData.codigo,
      descripcion: formData.descripcion,
      tipo: formData.tipo,
      estado: 'A'
    };

    // Si estamos editando, procedemos directamente con la actualización
    if (this.editIndex !== null) {
      this.actualizarConcepto(conceptoData);
      return;
    }

    // Si es un nuevo registro, verificamos primero si existe
    showSpinner();
    this.isLoading = true;

    this.conceptoService.existeConcepto(this.DEFAULT_EMPRESA, conceptoData.codigo, 'JA')
      .pipe(
        finalize(() => {
          this.isLoading = false;
          hideSpinner();
        })
      )
      .subscribe({
        next: (existe) => {
          if (existe) {
            this.notificationService.warningMessage(
              'Código duplicado',
              `Ya existe un concepto con el código ${conceptoData.codigo}`
            );
          } else {
            this.crearNuevoConcepto(conceptoData);
          }
        },
        error: (error) => {
          this.notificationService.errorMessage(
            'Error',
            'No se pudo verificar si el concepto ya existe'
          );
        }
      });
  }

  private actualizarConcepto(conceptoData: ConceptoComision): void {
    showSpinner();
    this.isLoading = true;

    this.conceptoService.updateConcepto(
      this.DEFAULT_EMPRESA,
      conceptoData.codigo,
      conceptoData,
      'JA'
    ).pipe(
      finalize(() => {
        this.isLoading = false;
        hideSpinner();
      })
    ).subscribe({
      next: () => {
        this.notificationService.successMessage('Concepto actualizado correctamente');
        this.resetForm();
        this.cargarRegistros(false);
      },
      error: (error) => {
        this.notificationService.errorMessage('Error al actualizar el concepto', error.message);
      }
    });
  }

  private crearNuevoConcepto(conceptoData: ConceptoComision): void {
    showSpinner();
    this.isLoading = true;

    this.conceptoService.createConcepto(conceptoData, 'JA')
      .pipe(
        finalize(() => {
          this.isLoading = false;
          hideSpinner();
        })
      )
      .subscribe({
        next: () => {
          this.notificationService.successMessage('Concepto creado correctamente');
          this.resetForm();
          this.cargarRegistros(false);
        },
        error: (error) => {
          this.notificationService.errorMessage('Error al crear el concepto', error.message);
        }
      });
  }

  onEditar(concepto: ConceptoComision): void {
    if (this.isCodigoProtegido(concepto.codigo)) {
      this.notificationService.warningMessage('Este registro no puede ser editado porque es un concepto base del sistema.');
      return;
    }

    this.editIndex = this.registros.data.findIndex(r => r.codigo === concepto.codigo);
    this.comisionForm.patchValue({
      codigo: concepto.codigo,
      descripcion: concepto.descripcion,
      tipo: concepto.tipo
    });
    // Deshabilitar el campo código en modo edición usando el FormControl
    this.comisionForm.get('codigo')?.disable();
  }

  onEliminar(concepto: ConceptoComision): void {
    if (this.isCodigoProtegido(concepto.codigo)) {
      this.notificationService.warningMessage('Este registro no puede ser inactivado porque es un concepto base del sistema.');
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '350px',
      data: { message: '¿Está seguro de inactivar este concepto?' }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        showSpinner();
        this.isLoading = true;
        
        // Crear objeto con estado inactivo
        const conceptoInactivo: ConceptoComision = {
          ...concepto,
          estado: 'I'
        };

        this.conceptoService.updateConcepto(this.DEFAULT_EMPRESA, conceptoInactivo.codigo, conceptoInactivo, 'JA')
        .pipe(
          finalize(() => {
            this.isLoading = false;
            hideSpinner();
          })
        ).subscribe({
          next: () => {
            this.notificationService.successMessage('Concepto inactivado correctamente');
            this.cargarRegistros(false);
          },
          error: (error) => {
            this.notificationService.errorMessage('Error al inactivar el concepto');
          }
        });
      }
    });
  }

  // Función para verificar si un código está protegido
  isCodigoProtegido(codigo: string): boolean {
    return this.CODIGOS_PROTEGIDOS.includes(codigo);
  }

  // Método para verificar si se puede editar un registro
  canEdit(registro: ConceptoComision): boolean {
    return !this.isCodigoProtegido(registro.codigo);
  }

  // Método para verificar si se puede eliminar un registro
  canDelete(registro: ConceptoComision): boolean {
    return !this.isCodigoProtegido(registro.codigo);
  }

  resetForm(): void {
    this.editIndex = null;
    this.comisionForm.reset();
    // Habilitar el campo código al resetear
    this.comisionForm.get('codigo')?.enable();
    Object.keys(this.comisionForm.controls).forEach(key => {
      const control = this.comisionForm.get(key);
      control?.markAsUntouched();
      control?.markAsPristine();
      control?.setErrors(null);
    });
  }

  private configurarBuscador(): void {
    this.searchControl.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged()
      )
      .subscribe(value => {
        const searchValue = value?.toLowerCase() || '';
        this.registros.filter = searchValue;
        this.registros.filterPredicate = (data: ConceptoComision, filter: string) => {
          return data.codigo.toLowerCase().includes(filter) ||
                 data.descripcion.toLowerCase().includes(filter) ||
                 data.tipo.toLowerCase().includes(filter);
        };
      });
  }
}
